/**
 * Firmware Service
 * Handles loading and validating firmware files
 */

import { FIRMWARE_MANIFEST, getFirmwareURL, type FirmwareMetadata } from './FirmwareManifest'

export class FirmwareService {
  /**
   * Load firmware file from bundled assets
   */
  static async loadFirmware(metadata: FirmwareMetadata): Promise<File> {
    const url = getFirmwareURL(metadata.filename)

    try {
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Failed to load firmware: ${response.statusText}`)
      }

      const blob = await response.blob()

      // Validate size
      if (blob.size !== metadata.size) {
        console.warn(
          `Firmware size mismatch: expected ${metadata.size}, got ${blob.size}`
        )
      }

      // Create File object
      const file = new File([blob], metadata.filename, {
        type: 'application/octet-stream'
      })

      return file
    } catch (error) {
      throw new Error(
        `Failed to load firmware ${metadata.filename}: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Validate firmware file
   */
  static validateFirmware(file: File, metadata?: FirmwareMetadata): {
    valid: boolean
    errors: string[]
  } {
    const errors: string[] = []

    // Check file extension
    if (!file.name.endsWith('.bin')) {
      errors.push('Firmware file must have .bin extension')
    }

    // Check size constraints
    const MIN_SIZE = 100 * 1024 // 100 KB
    const MAX_SIZE = 1920 * 1024 // 1920 KB (partition size)

    if (file.size < MIN_SIZE) {
      errors.push(`Firmware file too small (minimum ${MIN_SIZE / 1024} KB)`)
    }

    if (file.size > MAX_SIZE) {
      errors.push(`Firmware file too large (maximum ${MAX_SIZE / 1024} KB)`)
    }

    // If metadata provided, check size matches
    if (metadata && file.size !== metadata.size) {
      errors.push(
        `File size mismatch: expected ${metadata.size} bytes, got ${file.size} bytes`
      )
    }

    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * Calculate SHA-256 hash of firmware file
   */
  static async calculateSHA256(file: File): Promise<string> {
    const buffer = await file.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  }

  /**
   * Verify firmware SHA-256 hash
   */
  static async verifyFirmware(
    file: File,
    expectedSHA256: string
  ): Promise<boolean> {
    const actualSHA256 = await this.calculateSHA256(file)
    return actualSHA256.toLowerCase() === expectedSHA256.toLowerCase()
  }

  /**
   * Get all available firmware versions
   */
  static async getAvailableFirmwares(): Promise<FirmwareMetadata[]> {
    const manifest = await FIRMWARE_MANIFEST.get()
    return manifest.available
  }

  /**
   * Get latest firmware version
   */
  static async getLatestVersion(): Promise<string> {
    const manifest = await FIRMWARE_MANIFEST.get()
    return manifest.latestVersion
  }

  /**
   * Format file size for display
   */
  static formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  /**
   * Format version for display
   */
  static formatVersion(version: string): string {
    return `v${version}`
  }
}
